import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import Upload from '@components/upload';

import AppState from '@webapp/store/state';
import {
  CreateAsset,
  CancelAssetUpload,
} from '../../../store/assets/action-creators';
import { AssetUpload } from '@webapp/types/assets/asset-upload';
import { UploadError } from '@webapp/types/assets/upload-error';
import * as assetsSelectors from '@webapp/store/assets/selectors';
import { getAssetTrackingId } from '../../../utils/assets/get-tracking-id';
import { ImageData } from '@webapp/types/common/image-data';
import UploadItem from '@webapp/components/upload/upload-item';
import SquareImg from '@components/basics/artwork-img';
import styled from 'styled-components';

interface UploadVideoCoverCtrlProps {
  className?: string;
  inline?: boolean;
  totalProgress: number;
  generalStatus: AssetUpload['status'];
  errors: UploadError[];
  error?: React.ReactNode;
  trackingId?: string;
  uploaded: number;
  total: number;
  createAsset: (file: File, trackingId: string) => CreateAsset;
  onUpload: (trackingId: string) => void;
  onDataUrl: (url: string) => void;
  onError: (message: string) => void;
  cancelUpload: (trackingId: string) => CancelAssetUpload;
}

const readImage = (
  f: File,
  onDataUrl: (url: string) => void
): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target && e.target.result) {
        const img = new Image();
        const src = e.target.result.toString();

        onDataUrl(src);

        img.onload = () => {
          resolve({
            src,
            width: img.width,
            height: img.height,
            type: f.type,
          });
        };

        img.onerror = () => reject(new Error('MUSIC_VIDEO.LOADING_ERROR'));
        img.src = src;
      }
    };
    reader.readAsDataURL(f);
  });
};

const validateImageFormat = (file: File) => {
  if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
    throw new Error('MUSIC_VIDEO.INVALID_FORMAT');
  }
};

const validateImageSize = (image: ImageData) => {
  if (
    image.width < 1920 ||
    image.height < 1080 ||
    image.width / image.height !== 16 / 9
  ) {
    throw new Error('MUSIC_VIDEO.INVALID_SIZE');
  }
};

const mapStateToProps = (
  state: AppState,
  props: Pick<UploadVideoCoverCtrlProps, 'trackingId'>
) => ({
  totalProgress: props.trackingId
    ? assetsSelectors.totalProgress(state.assets, props.trackingId)
    : 0,
  generalStatus: props.trackingId
    ? assetsSelectors.generalStatus(state.assets, props.trackingId)
    : undefined,
  errors: props.trackingId
    ? assetsSelectors.uploadErrors(state.assets, props.trackingId)
    : [],
  uploaded: props.trackingId
    ? assetsSelectors.getUploaded(state.assets, props.trackingId)
    : 0,
  total: props.trackingId
    ? assetsSelectors.getTotalUploads(state.assets, props.trackingId)
    : 0,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createAsset: (file: File, trackingId: string) =>
    dispatch(new CreateAsset('video-preview', file, undefined, trackingId)),
  cancelUpload: (trackingId: string) =>
    dispatch(new CancelAssetUpload(trackingId)),
});

export const UploadVideoCoverCtrl: React.FC<UploadVideoCoverCtrlProps> = (
  props
) => {
  const propError = props.error;

  const [filename, setFilename] = React.useState<string>();
  const [imgUrl, setImgUrl] = React.useState<string>();
  const [error, setError] = React.useState<React.ReactNode>();
  const [errorImgFormat, setErrorImgFormat] = React.useState(false);
  const [errorImgSize, setErrorImgSize] = React.useState(false);

  React.useEffect(() => {
    setError(propError);
  }, [propError]);

  const handleFile = async (file: File) => {
    const trackingId = getAssetTrackingId();

    setError(undefined);
    setErrorImgFormat(false);
    setErrorImgSize(false);
    try {
      validateImageFormat(file);
      const image = await readImage(file, props.onDataUrl);
      setImgUrl(image.src);
      validateImageSize(image);

      setFilename(file.name);
      props.createAsset(file, trackingId);
      props.onUpload(trackingId);
    } catch (e) {
      switch (e.message) {
        case 'MUSIC_VIDEO.INVALID_SIZE': {
          setErrorImgSize(true);
          break;
        }
        case 'MUSIC_VIDEO.INVALID_FORMAT': {
          setErrorImgFormat(true);
          break;
        }
        default: {
          setError(e ? e.message : 'MUSIC_VIDEO.UNKNOWN_ERROR');
        }
      }
      props.onError(e ? e.message : 'MUSIC_VIDEO.UNKNOWN_ERROR');
    }
  };

  const getLabel = () => {
    switch (props.generalStatus) {
      case 'uploading':
        return 'Uploading...';
      case 'done':
        return 'Uploaded';
      default:
        return (
          <span>
            <span className={errorImgSize ? 'error' : ''}>
              Size: 1920x1080px
            </span>
            <br />
            <span className={errorImgFormat ? 'error' : ''}>
              Formats: .jpg, .png
            </span>
          </span>
        );
    }
  };

  return (
    <Upload
      className={props.className}
      status={props.generalStatus}
      progress={props.totalProgress}
      error={error || errorImgSize || errorImgFormat}
      inline={props.inline}
      onChange={(files: File[]) => (files.length ? handleFile(files[0]) : null)}
    >
      <UploadItem
        inline
        buttonType="secondary-light"
        title="Video Preview Image"
        label={getLabel()}
        icon={
          <SquareImg
            className={`artwork ${imgUrl ? 'show' : ''}`}
            size={48}
            srcImg={imgUrl}
          />
        }
        accept="image/*"
        status={props.generalStatus}
        error={error}
        filename={filename}
        onChange={(files: FileList) =>
          files?.length ? handleFile(files[0]) : undefined
        }
        onCancel={() => {
          if (props.trackingId) {
            props.cancelUpload(props.trackingId);
          }

          setFilename('');
          setImgUrl('');
        }}
      />
    </Upload>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(UploadVideoCoverCtrl)`
  .artwork {
    margin-right: 20px;
    flex-shrink: 0;
    background: ${({ theme }) => theme.colors.menuBackground};
    display: none;

    &.show {
      display: block;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
  }
`);
